import { useMemo } from 'react';
import round from 'lodash/round';
import isNumber from 'lodash/isNumber';
import { formatMoneyValue } from '../../../../util';
import { PortfolioSecurity } from '../../../4-module/widgets/portfolio/components/interfaces';
import TaxRank from '../../../4-module/widgets/portfolio/components/taxRank';
import { Box, Typography } from '../../../1-primative';
import { Tooltip } from '../../../2-component';

type ColumnData = {
  data: PortfolioSecurity
};

const SecurityColumn = ({ data }: ColumnData) => {
  const { ticker, financialProductTaxRank, translatedName } = data;

  return (<Box display='flex' justifyContent='start' alignItems='center' gap='4px'>
    {financialProductTaxRank && <TaxRank taxRank={financialProductTaxRank} />}

    <Typography>
      <Box display='flex' justifyContent='start' flexDirection='column'>
        <Tooltip title={translatedName}>{ticker}</Tooltip>

        {formatMoneyValue(data.currentPriceCents)}
      </Box>
    </Typography>
  </Box>);
};

const CurrentCashColumn = ({ data }: ColumnData) => (<div>{ formatMoneyValue(round(data.valueCents) || 0) }</div>);
const CurrentPercentColumn = ({ data }: ColumnData) => {
  const { expectedValueCents, valueCents } = data;

  const valuePercent = useMemo(() => {
    if (!isNumber(valueCents) || !isNumber(expectedValueCents) || expectedValueCents <= 0) { return 0; }
    const percentage = valueCents / expectedValueCents;
    return !isFinite(percentage || 0) ? '100.00' : round((percentage || 0) * 100, 2).toFixed(2);
  }, [expectedValueCents, valueCents]);

  return (<div>{ valuePercent }%</div>);
};
const CurrentHashColumn = ({ data }: ColumnData) => (<div>CurrentHashColumn</div>);

const TargetCashColumn = ({ data }: ColumnData) => (<div>{ formatMoneyValue(round(data.expectedValueCents) || 0) }</div>);
const TargetPercentColumn = ({ data }: ColumnData) => (<div>{ round((data.expectedPercentage || 0) * 100, 2).toFixed(2) }%</div>);
const TargetHashColumn = ({ data }: ColumnData) => (<div>TargetHashColumn</div>);

const DriftCashColumn = ({ data }: ColumnData) => {
  const {
    expectedValueCents, valueCents, id, subAccounts,
  } = data;

  const valueCash = useMemo(() => {
    if (id !== 'CASH') { return round(valueCents - expectedValueCents) || 0; }
    const drift: number = subAccounts.reduce((total: number, subAccount) => total + (subAccount.cashCents - subAccount.cashOnHoldToTradeCents), 0);
    return (round(valueCents - expectedValueCents) || 0) + drift;
  }, [expectedValueCents, valueCents, id, subAccounts]);

  return (
    <div>{ formatMoneyValue(valueCash) }</div>
  );
};

const DriftPercentColumn = ({ data }: ColumnData) => {
  const { expectedValueCents, valueCents } = data;

  const valuePercent = useMemo(() => {
    if (!isNumber(valueCents) || !isNumber(expectedValueCents)) { return 0; }
    const percentage = (valueCents - expectedValueCents) / expectedValueCents;
    return !isFinite(percentage || 0) ? '100.00' : round((percentage || 0) * 100, 2).toFixed(2);
  }, [expectedValueCents, valueCents]);

  return (<div>{ valuePercent }%</div>);
};

const DriftHashColumn = ({ data }: ColumnData) => (<div>DriftHashColumn</div>);

export {
  SecurityColumn, CurrentCashColumn, CurrentPercentColumn, CurrentHashColumn, TargetCashColumn, TargetPercentColumn, TargetHashColumn, DriftCashColumn, DriftPercentColumn, DriftHashColumn,
};
